import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    BimModifyContractOtherCostMutationVariables,
    BimUpdateContractOtherCostMutationVariables,
    OtherCostCalculationTypeEnum,
    UerUpdateContractOtherCostToWorkOrderFromPriceListMutationVariables,
    UerUpdateOtherCostToActKc2MutationVariables,
} from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
    bimModifyContractOtherCostGql,
    bimUpdateContractOtherCostGql,
    uerUpdateContractOtherCostToWorkOrderFromPriceList,
    uerUpdateOtherCostToActKC2,
} from '../editOtherQueries';
import { getUpdateInput } from '../utils';

import type { IUseUpdateContractOtherCost } from './useUerUpdateContractOtherCost';

const requestIdBimUpdateContractOtherCost = 'bimUpdateContractOtherCost';
const requestIdUerUpdateOtherCostToActKC2 = 'uerUpdateOtherCostToActKC2';
const requestIdBimModifyContractOtherCost = 'bimModifyContractOtherCost';
const requestIdUerUpdateContractOtherCostToWorkOrderFromPriceList =
    'uerUpdateContractOtherCostToWorkOrderFromPriceList';

export const useBimUpdateContractOtherCost = (props: IUseUpdateContractOtherCost) => {
    const {
        contractId,
        selectedChapterOrSection,
        selectedType,
        workOrderId,
        workOrderStatus,
        workOrderOtherPriceId,
        isCalculationTypeEditable,
        isOtherFromCatalog,
        isUpdateContractCondition,
        otherId,
        isOnlyTypeEditAvailable,
    } = props;
    const dispatch = useDispatch();

    const isUpdateToAct = workOrderId && workOrderStatus === WorkOrderStatusEnum.InProgress;

    const [updateContractOtherCostData, updateContractOtherCostStatus, , updateContractOtherCostRequest] = useRequest({
        requestId: requestIdBimUpdateContractOtherCost,
        query: bimUpdateContractOtherCostGql,
        withEffect: false,
    });

    const [updateOtherCostToActKC2Data, updateOtherCostToActKC2Status, , updateOtherCostToActKC2Request] = useRequest({
        requestId: requestIdUerUpdateOtherCostToActKC2,
        query: uerUpdateOtherCostToActKC2,
        withEffect: false,
    });

    const [updateBimOtherCostToActKC2Data, updateBimOtherCostToActKC2Status, , updateBimOtherCostToActKC2Request] =
        useRequest({
            requestId: requestIdBimModifyContractOtherCost,
            query: bimModifyContractOtherCostGql,
            withEffect: false,
        });

    const [
        updateContractOtherCostToWorkOrderFromPriceListData,
        updateContractOtherCostToWorkOrderFromPriceListStatus,
        ,
        updateContractOtherCostToWorkOrderFromPriceListRequest,
    ] = useRequest({
        requestId: requestIdUerUpdateContractOtherCostToWorkOrderFromPriceList,
        query: uerUpdateContractOtherCostToWorkOrderFromPriceList,
        withEffect: false,
    });

    const requestId = useMemo(() => {
        if (isOnlyTypeEditAvailable) {
            return requestIdBimModifyContractOtherCost;
        }

        if (isUpdateToAct) {
            return requestIdUerUpdateOtherCostToActKC2;
        }

        if (isUpdateContractCondition || isOtherFromCatalog) {
            return requestIdBimUpdateContractOtherCost;
        }

        return requestIdUerUpdateContractOtherCostToWorkOrderFromPriceList;
    }, [isOnlyTypeEditAvailable, isOtherFromCatalog, isUpdateContractCondition, isUpdateToAct]);

    const [reset] = useResetStoreData({ requestId });

    const data = useMemo(() => {
        if (isOnlyTypeEditAvailable) {
            return updateBimOtherCostToActKC2Data?.bimModifyContractOtherCost;
        }

        if (isUpdateToAct) {
            return updateOtherCostToActKC2Data?.uerUpdateOtherCostToActKC2;
        }

        if (isUpdateContractCondition || isOtherFromCatalog) {
            return updateContractOtherCostData?.bimUpdateContractOtherCost;
        }

        return updateContractOtherCostToWorkOrderFromPriceListData?.uerUpdateContractOtherCostToWorkOrderFromPriceList;
    }, [
        isOnlyTypeEditAvailable,
        isOtherFromCatalog,
        isUpdateContractCondition,
        isUpdateToAct,
        updateBimOtherCostToActKC2Data?.bimModifyContractOtherCost,
        updateContractOtherCostData?.bimUpdateContractOtherCost,
        updateContractOtherCostToWorkOrderFromPriceListData?.uerUpdateContractOtherCostToWorkOrderFromPriceList,
        updateOtherCostToActKC2Data?.uerUpdateOtherCostToActKC2,
    ]);

    const status = useMemo(() => {
        if (isOnlyTypeEditAvailable) {
            return updateBimOtherCostToActKC2Status;
        }

        if (isUpdateToAct) {
            return updateOtherCostToActKC2Status;
        }

        if (isUpdateContractCondition || isOtherFromCatalog) {
            return updateContractOtherCostStatus;
        }

        return updateContractOtherCostToWorkOrderFromPriceListStatus;
    }, [
        isOnlyTypeEditAvailable,
        isOtherFromCatalog,
        isUpdateContractCondition,
        isUpdateToAct,
        updateBimOtherCostToActKC2Status,
        updateContractOtherCostStatus,
        updateContractOtherCostToWorkOrderFromPriceListStatus,
        updateOtherCostToActKC2Status,
    ]);

    const makeRequest = useCallback(
        (formData: any, limitsArray: any[], formula: string) => {
            const { updateInput, chapter, section } = getUpdateInput({
                data: formData,
                contractId,
                selectedChapterOrSection,
                selectedType,
                isCalculationTypeEditable,
                otherId,
                limitsArray,
            });

            if (isOnlyTypeEditAvailable) {
                const variables: BimModifyContractOtherCostMutationVariables = {
                    input: {
                        catalogOtherCostTypeId: updateInput.catalogOtherCostTypeId,
                        contractId: updateInput.contractId,
                        formula,
                        id: updateInput.id,
                    },
                };

                dispatch(updateBimOtherCostToActKC2Request(variables));

                return;
            }

            if (isUpdateToAct) {
                const variables: UerUpdateOtherCostToActKc2MutationVariables = {
                    workOrderOtherPriceId,
                    updateInput,
                };

                dispatch(updateOtherCostToActKC2Request(variables));

                return;
            }

            if (isUpdateContractCondition || isOtherFromCatalog) {
                const variables: BimUpdateContractOtherCostMutationVariables = {
                    input: {
                        ...updateInput,
                        otherCostLimits: updateInput.limits,
                        calculatingType: updateInput.calculatingType as OtherCostCalculationTypeEnum,
                        chapter,
                        section,
                        formula,
                    },
                };

                dispatch(updateContractOtherCostRequest(variables));

                return;
            }

            const variables: UerUpdateContractOtherCostToWorkOrderFromPriceListMutationVariables = {
                workOrderOtherPriceId,
                input: updateInput,
            };

            dispatch(updateContractOtherCostToWorkOrderFromPriceListRequest(variables));
        },
        [
            contractId,
            dispatch,
            isCalculationTypeEditable,
            isOnlyTypeEditAvailable,
            isOtherFromCatalog,
            isUpdateContractCondition,
            isUpdateToAct,
            otherId,
            selectedChapterOrSection,
            selectedType,
            updateBimOtherCostToActKC2Request,
            updateContractOtherCostRequest,
            updateContractOtherCostToWorkOrderFromPriceListRequest,
            updateOtherCostToActKC2Request,
            workOrderOtherPriceId,
        ],
    );

    return { data, status, reset, makeRequest };
};
